import { useState } from 'react';
import { Pill, formatDkk, type PillVariant } from '@jywer/ui';
import { approveEntry, markEntryPaid, reverseEntry, setOverride } from './api';
import type { CommissionEntryRow } from './types';

const STATUS_PILL: Record<string, { variant: PillVariant; label: string }> = {
  pending: { variant: 'warning', label: 'Afventer' },
  approved: { variant: 'success', label: 'Godkendt' },
  paid: { variant: 'info', label: 'Udbetalt' },
  reversed: { variant: 'muted', label: 'Tilbageført' },
};

const LEAD_SOURCE_LABEL: Record<string, string> = {
  cold_new_client: 'Cold',
  inbound_new_client: 'Inbound',
  existing_client: 'Eksisterende',
};

interface Props {
  entry: CommissionEntryRow;
  onUpdated: (next: CommissionEntryRow) => void;
  onReversed: (original: CommissionEntryRow, reversal: CommissionEntryRow) => void;
}

export function CommissionEntryRowDetail({ entry, onUpdated, onReversed }: Props) {
  const [overrideInput, setOverrideInput] = useState<string>(entry.override_amount ?? '');
  const [overrideReason, setOverrideReason] = useState<string>(entry.override_reason ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reverseReason, setReverseReason] = useState('');

  const statusPill = STATUS_PILL[entry.status] ?? {
    variant: 'neutral' as PillVariant,
    label: entry.status,
  };

  const handleSaveOverride = async () => {
    setError(null);
    setBusy(true);
    try {
      const n = overrideInput.trim() === '' ? null : Number(overrideInput);
      if (n !== null && !Number.isFinite(n)) throw new Error('Ugyldigt beløb');
      const updated = await setOverride(entry.id, n, n === null ? null : overrideReason);
      onUpdated(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke gemme override');
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = async () => {
    setError(null);
    setBusy(true);
    try {
      const updated = await approveEntry(entry.id);
      onUpdated(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke godkende');
    } finally {
      setBusy(false);
    }
  };

  const handleMarkPaid = async () => {
    setError(null);
    setBusy(true);
    try {
      const updated = await markEntryPaid(entry.id);
      onUpdated(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke markere som betalt');
    } finally {
      setBusy(false);
    }
  };

  const handleReverse = async () => {
    if (!reverseReason.trim()) {
      setError('Årsag påkrævet');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const result = await reverseEntry(entry.id, reverseReason.trim());
      onReversed(result.entry, result.reversal);
      setReverseReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke tilbageføre');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded border border-line-1 bg-paper-0 p-3 text-sm">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <span className="text-xs uppercase tracking-wide text-ink-2">
          {entry.entity_type === 'project' ? 'Projekt' : 'Abonnement'}
        </span>
        <span className="font-medium text-primary">{entry.entity_title ?? '—'}</span>
        <span className="text-ink-2">{entry.client_name}</span>
        <Pill variant={statusPill.variant}>{statusPill.label}</Pill>
        <span className="text-xs text-ink-2">
          {LEAD_SOURCE_LABEL[entry.lead_source] ?? entry.lead_source}
        </span>
        <span className="text-xs text-ink-2">
          {entry.is_primary_rep ? 'Primær sælger' : 'Ekstra sælger'} · {entry.share_pct}%
        </span>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 md:grid-cols-4">
        <KV label="Beregningsgrundlag" value={formatDkk(entry.commission_base)} />
        <KV label="Sats" value={`${(Number(entry.commission_rate) * 100).toFixed(1)}%`} />
        <KV label="Beregnet" value={formatDkk(entry.calculated_amount)} />
        <KV
          label="Godkendt"
          value={entry.approved_amount === null ? '—' : formatDkk(entry.approved_amount)}
        />
      </dl>

      {entry.status === 'pending' ? (
        <div className="mt-3 flex flex-col gap-2 border-t border-line-1 pt-3">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-xs text-ink-2">Override beløb (DKK)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={overrideInput}
                onChange={(e) => setOverrideInput(e.target.value)}
                className="w-full rounded border border-line-2 px-2 py-1 text-sm"
                placeholder="Lad stå tomt for at bruge beregnet"
                disabled={busy}
              />
            </div>
            <div className="flex-[2]">
              <label className="text-xs text-ink-2">Begrundelse</label>
              <input
                type="text"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                className="w-full rounded border border-line-2 px-2 py-1 text-sm"
                placeholder="Påkrævet hvis override sat"
                disabled={busy}
              />
            </div>
            <button
              type="button"
              onClick={handleSaveOverride}
              disabled={busy}
              className="rounded border border-line-2 bg-paper-0 px-3 py-1.5 text-sm hover:border-primary disabled:opacity-50"
            >
              Gem override
            </button>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleApprove}
              disabled={busy}
              className="rounded bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary/90 disabled:opacity-50"
            >
              Godkend
            </button>
          </div>
        </div>
      ) : entry.status === 'approved' ? (
        <div className="mt-3 flex items-center gap-2 border-t border-line-1 pt-3">
          <button
            type="button"
            onClick={handleMarkPaid}
            disabled={busy}
            className="rounded bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary/90 disabled:opacity-50"
          >
            Marker som udbetalt
          </button>

          <input
            type="text"
            value={reverseReason}
            onChange={(e) => setReverseReason(e.target.value)}
            placeholder="Årsag til tilbageførsel"
            className="flex-1 rounded border border-line-2 px-2 py-1 text-sm"
            disabled={busy}
          />
          <button
            type="button"
            onClick={handleReverse}
            disabled={busy}
            className="rounded border border-status-err px-3 py-1.5 text-sm text-status-err hover:bg-status-err/5 disabled:opacity-50"
          >
            Tilbagefør
          </button>
        </div>
      ) : null}

      {entry.override_reason && entry.status !== 'pending' ? (
        <div className="mt-2 text-xs text-ink-2">Override begrundelse: {entry.override_reason}</div>
      ) : null}

      {error ? <div className="mt-2 text-xs text-status-err">{error}</div> : null}
    </div>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs text-ink-2">{label}</dt>
      <dd className="tabular-nums text-ink-1">{value}</dd>
    </div>
  );
}
